import { useCallback, useEffect, useRef, useState } from "react";
import { BUILD_VERSION } from "common/services";

/** How often to re-check while the tab is open. */
const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;
/** Floor between checks, so tab-switching a lot cannot turn into a request storm. */
const MIN_GAP_MS = 60 * 1000;

export interface VersionCheck {
  /** True once `/version.json` reports a build id different from the running bundle. */
  updateAvailable: boolean;
  /** The build id currently published on the CDN, when known. */
  latestVersion?: string;
  /** Hard-reloads to pick up the new index.html (and with it, the new hashed bundles). */
  reload: () => void;
  /** Hides the prompt for this tab without reloading. */
  dismiss: () => void;
}

/**
 * Detects that a newer build has been deployed while this tab was open.
 *
 * <p>Why this exists: Vite content-hashes every JS/CSS asset, so those are safe to cache
 * forever — the only document that must never be cached hard is `index.html`, because it is
 * what points at the current hashes. Fixing the `Cache-Control` headers at CloudFront (see
 * `docs/CACHE_BUSTING.md`) makes a normal page load pick up a new build with no user action.
 * It does nothing for a single-page app tab that was opened days ago and never re-requests
 * the document — that tab keeps running the old bundle indefinitely. This hook closes that
 * gap by polling a tiny unhashed `/version.json` and surfacing a reload prompt.
 *
 * <p>The fetch uses `cache: "no-store"` so it bypasses the browser HTTP cache, and appends a
 * unique query param so any intermediate proxy that ignores `no-store` still misses. That
 * makes the check correct even before the CloudFront cache policy is in place.
 *
 * <p>It deliberately never reloads on its own: a rep can be mid-quote with unsaved form
 * state, and silently swapping the bundle out from under them would lose it.
 */
export const useVersionCheck = (options?: {
  intervalMs?: number;
  enabled?: boolean;
}): VersionCheck => {
  const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;
  // Off in dev by default: the local build id never changes, so it is pure noise.
  const enabled = options?.enabled ?? import.meta.env.PROD;

  const [latestVersion, setLatestVersion] = useState<string>();
  const [dismissed, setDismissed] = useState(false);
  const lastCheckedAt = useRef(0);

  const check = useCallback(async (signal?: AbortSignal) => {
    const now = Date.now();
    if (now - lastCheckedAt.current < MIN_GAP_MS) return;
    lastCheckedAt.current = now;
    try {
      const url = `${import.meta.env.BASE_URL}version.json?t=${now}`.replace(/\/\/+/g, "/");
      const res = await fetch(url, { cache: "no-store", signal });
      if (!res.ok) return;
      const body: { version?: string } = await res.json();
      if (body?.version) setLatestVersion(body.version);
    } catch {
      // Offline, blocked by a corporate proxy, or a bad deploy that left no version.json —
      // none of those are worth surfacing. The next tick tries again.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    void check(controller.signal);

    const timer = window.setInterval(() => void check(controller.signal), intervalMs);
    // Coming back to a parked tab is the single most likely moment to be stale, so check
    // then too rather than waiting out the remainder of the interval.
    const onWake = () => {
      if (document.visibilityState === "visible") void check(controller.signal);
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [check, enabled, intervalMs]);

  const reload = useCallback(() => {
    // `location.reload()` revalidates the top-level document, so it picks up the new
    // index.html even if the browser had heuristically cached the old one.
    window.location.reload();
  }, []);

  return {
    updateAvailable: !dismissed && !!latestVersion && latestVersion !== BUILD_VERSION,
    latestVersion,
    reload,
    dismiss: useCallback(() => setDismissed(true), []),
  };
};
